import { useCallback, useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useForm, Controller } from 'react-hook-form';
import {
  IconLoader,
  ButtonPrimary,
  ToggleInput,
  ResponseError,
  responseErrorsResolver,
  getResponseErrors,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  useEditBeneficiarySettingsMutation,
  useLazyGetPSPDetailsQuery,
} from '../settings/store/services/details';
import {
  TransactionsSettingsKeys,
  BeneficiariesSettingsVormValues,
} from '../settings/types';

import cn from '../settings/styles.module.css';

const BeneficiariesSettings = () => {
  const [defaultFormValues, setDefaultFormValues] =
    useState<BeneficiariesSettingsVormValues>();

  const [getBeneficiariesSettings, { isLoading, isFetching }] =
    useLazyGetPSPDetailsQuery();
  const [edit, { isLoading: editIsLoading }] =
    useEditBeneficiarySettingsMutation();
  const [globalError, setGlobalError] = useState('');
  const [errorList, setErrorList] = useState('');

  const {
    settings: {
      details: { pspDetails },
    },
  } = useAppSelector((state) => state);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<BeneficiariesSettingsVormValues>();

  const fields = watch();

  const isDirty = !isEqual(fields, defaultFormValues);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const updatedSettings: {
      [key: string]:
        | string
        | boolean
        | string[]
        | Record<string, string>
        | Record<string, boolean>;
      key: TransactionsSettingsKeys;
    }[] = [];

    if (
      values.beneficiariesContractsEnabled !==
      defaultFormValues?.beneficiariesContractsEnabled
    ) {
      updatedSettings.push({
        beneficiariesContractsEnabled:
          values.beneficiariesContractsEnabled as boolean,
        key: TransactionsSettingsKeys.CORPORATE_BENEFICIARIES_CONTRACTS_ENABLED,
      });
    }

    if (
      values.beneficiariesContractsModerationEnabled !==
      defaultFormValues?.beneficiariesContractsModerationEnabled
    ) {
      updatedSettings.push({
        beneficiariesContractsModerationEnabled:
          values.beneficiariesContractsModerationEnabled as boolean,
        key: TransactionsSettingsKeys.CORPORATE_BENEFICIARIES_CONTRACTS_MODERATION_ENABLED,
      });
    }

    if (
      values.beneficiariesModerationEnabled !==
      defaultFormValues?.beneficiariesModerationEnabled
    ) {
      updatedSettings.push({
        beneficiariesModerationEnabled:
          values.beneficiariesModerationEnabled as boolean,
        key: TransactionsSettingsKeys.CORPORATE_BENEFICIARIES_MODERATION_ENABLED,
      });
    }

    if (updatedSettings.length > 0) {
      const response = (await Promise.all(
        updatedSettings.map(async (item) => {
          const res = await edit(item);

          return res;
        }),
      )) as { error?: ResponseError }[];

      if (response.find((item) => item.error)) {
        const errorsResponse = response.filter((item) => item.error);

        errorsResponse.forEach((item) => {
          responseErrorsResolver({
            errors: item.error as ResponseError,
            setError,
            setGlobalError,
          });
        });
      }

      const body: BeneficiariesSettingsVormValues = {
        beneficiariesModerationEnabled: values.beneficiariesModerationEnabled,
        beneficiariesContractsEnabled: values.beneficiariesContractsEnabled,
        beneficiariesContractsModerationEnabled:
          values.beneficiariesContractsModerationEnabled,
      };

      setDefaultFormValues(body);
    }
  });

  const getBeneficiariesSettingsAction = useCallback(async () => {
    const response = (await getBeneficiariesSettings()) as {
      error?: ResponseError;
    };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList,
      });
    }
  }, []);

  useEffect(() => {
    if (!pspDetails) {
      getBeneficiariesSettingsAction();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (pspDetails) {
      const beneficiariesSettings = pspDetails.filter((item) =>
        item.key.includes('BENEFICIARIES'),
      );

      if (beneficiariesSettings) {
        const beneficiariesModerationEnabled = beneficiariesSettings?.find(
          (item) =>
            item.key ===
            TransactionsSettingsKeys.CORPORATE_BENEFICIARIES_MODERATION_ENABLED,
        )?.data?.value as boolean;
        const beneficiariesContractsEnabled = beneficiariesSettings?.find(
          (item) =>
            item.key ===
            TransactionsSettingsKeys.CORPORATE_BENEFICIARIES_CONTRACTS_ENABLED,
        )?.data?.value as boolean;
        const beneficiariesContractsModerationEnabled =
          beneficiariesSettings?.find(
            (item) =>
              item.key ===
              TransactionsSettingsKeys.CORPORATE_BENEFICIARIES_CONTRACTS_MODERATION_ENABLED,
          )?.data?.value as boolean;

        const body: BeneficiariesSettingsVormValues = {
          beneficiariesModerationEnabled,
          beneficiariesContractsEnabled,
          beneficiariesContractsModerationEnabled,
        };

        reset(body);
        setDefaultFormValues(body);
      }
    }
  }, [pspDetails]);

  useEffect(() => {
    let title = 'Transaction Settings: Beneficiaries';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  if (isLoading || isFetching) {
    content = (
      <div className={cn.beneficiariesSettingsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <div className={cn.beneficiariesSettingsContent}>
        {!errorList && (
          <>
            <div className={cn.beneficiariesSettingsBlock}>
              <div className={cn.beneficiariesSettingsTitle}>Beneficiaries</div>

              <div className={cn.beneficiariesSettingsDescription}>
                Beneficiaries are created without approval — they are
                immediately approved
              </div>
              <Controller
                control={control}
                name="beneficiariesModerationEnabled"
                render={({ field: { value, name, onChange } }) => (
                  <>
                    <ToggleInput
                      name={name}
                      title="Beneficiary approval during creation"
                      onChange={(e) => onChange(e.target.checked)}
                      checked={value}
                    />
                    {errors?.beneficiariesModerationEnabled?.message && (
                      <div className={cn.settingError}>
                        {errors?.beneficiariesModerationEnabled?.message}
                      </div>
                    )}
                  </>
                )}
              />
            </div>
            <div className={cn.beneficiariesSettingsBlock}>
              <div className={cn.beneficiariesSettingsTitle}>Contracts</div>
              <div className={cn.beneficiariesSettingsDescription}>
                The creation of contracts is disabled. Payments are created
                without contracts.
              </div>
              <div className={cn.formGroup}>
                <Controller
                  control={control}
                  name="beneficiariesContractsEnabled"
                  render={({ field: { value, name, onChange } }) => (
                    <>
                      <ToggleInput
                        name={name}
                        title="Creating contracts"
                        onChange={(e) => {
                          onChange(e.target.checked);

                          if (!e.target.checked) {
                            setValue(
                              'beneficiariesContractsModerationEnabled',
                              false,
                            );
                          }
                        }}
                        checked={value}
                      />
                      {errors?.beneficiariesContractsEnabled?.message && (
                        <div className={cn.settingError}>
                          {errors?.beneficiariesContractsEnabled?.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
              <div className={cn.formGroup}>
                <Controller
                  control={control}
                  name="beneficiariesContractsModerationEnabled"
                  render={({ field: { value, name, onChange } }) => (
                    <>
                      <ToggleInput
                        name={name}
                        title="Contract approval during creation"
                        onChange={(e) => onChange(e.target.checked)}
                        checked={value}
                        disabled={!fields.beneficiariesContractsEnabled}
                      />
                      {errors?.beneficiariesContractsModerationEnabled
                        ?.message && (
                        <div className={cn.settingError}>
                          {
                            errors?.beneficiariesContractsModerationEnabled
                              ?.message
                          }
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
            {globalError && (
              <div className={cn.saveGlobalError}>{globalError}</div>
            )}
            <ButtonPrimary
              text={editIsLoading ? 'Loading...' : 'Save'}
              className={cn.saveBeneficiariesSettings}
              disabled={!isDirty || editIsLoading}
              onClick={onSubmit}
              bold
            />
          </>
        )}
        {errorList && (
          <div className={cn.loadingError}>
            <div className={cn.loadingErrorTitle}>Unable to load data</div>
            <div className={cn.loadingErrorText}>{errorList}</div>
          </div>
        )}
      </div>
    );
  }

  return <div className={cn.beneficiariesSettingsWrapper}>{content}</div>;
};

export default BeneficiariesSettings;
